import { useEffect, useState } from "react";
import { LocalStorageKeyEnum } from "../../enums/localStorage";

// For any state that needs to be persisted between refreshes

export enum StateKeyEnum {
  SELECTED_MARKET_ONLY = "showSelectedMarketOnly",
  ORDERBOOK_VIEW_IN_USD = "orderbookViewInUSD",
  PREFERRED_MARKET_FILTER = "preferredMarketFilter",
  SHOW_BOTTOM_TRAY = "showBottomTray",
  ORDERBOOK_LAYOUT = "orderbookLayout",

  // Trade form uses underlying asset as the order size.
  // if unset, default is "USDC"
  TRADE_USE_USDC_TERMS = "tradeUseUSDCTerms",
}

type StorageType = {
  [key in StateKeyEnum]: string | number;
};

/**
 * localStorage is user-writable and survives across app versions, so its
 * contents are untrusted input. An unguarded JSON.parse here throws during
 * render and leaves a blank page that only clearing storage can fix, so a
 * corrupt blob is treated as absent instead.
 */
const readStore = (): StorageType => {
  try {
    const raw = localStorage.getItem(LocalStorageKeyEnum.PERSISTENT_STATE);
    if (!raw) return {} as StorageType;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as StorageType)
      : ({} as StorageType);
  } catch {
    return {} as StorageType;
  }
};

export const setPersistentState = (
  key: StateKeyEnum,
  value?: string | number
) => {
  const obj = readStore();
  if (value) {
    obj[key] = value;
  } else {
    delete obj[key];
  }
  try {
    localStorage.setItem(
      LocalStorageKeyEnum.PERSISTENT_STATE,
      JSON.stringify(obj)
    );
  } catch {
    // Storage full or blocked (private mode) — preferences are non-critical.
  }
};

export const getPersistentState = (key: StateKeyEnum) =>
  readStore()[key];

const usePersistentState = () => {
  const [showOnlyCurrentMarket, setShowOnlyCurrentMarket] = useState(
    Boolean(getPersistentState(StateKeyEnum.SELECTED_MARKET_ONLY))
  );
  const [orderbookViewInUSD, setOrderbookViewInUSD] = useState(
    Boolean(getPersistentState(StateKeyEnum.ORDERBOOK_VIEW_IN_USD))
  );
  const [preferredMarketFilter, setPreferredMarketFilter] = useState(
    getPersistentState(StateKeyEnum.PREFERRED_MARKET_FILTER)?.toString()
  );
  const [showBottomTray, setShowBottomTray] = useState(
    Boolean(getPersistentState(StateKeyEnum.SHOW_BOTTOM_TRAY))
  );
  const [orderbookLayout, setOrderbookLayout] = useState<string>(
    getPersistentState(StateKeyEnum.ORDERBOOK_LAYOUT)?.toString() || "tab"
  );

  const [tradeUseUSDCTerms, setTradeUseUSDCTerms] = useState(() => {
    const stored = getPersistentState(StateKeyEnum.TRADE_USE_USDC_TERMS);
    // Default to true (use USDC terms) if not set
    return stored !== undefined ? Boolean(stored) : true;
  });

  // Store to local storage whenever its updated
  useEffect(() => {
    setPersistentState(
      StateKeyEnum.SELECTED_MARKET_ONLY,
      showOnlyCurrentMarket ? 1 : 0
    );
  }, [showOnlyCurrentMarket]);

  useEffect(() => {
    setPersistentState(
      StateKeyEnum.ORDERBOOK_VIEW_IN_USD,
      orderbookViewInUSD ? 1 : 0
    );
  }, [orderbookViewInUSD]);

  useEffect(() => {
    setPersistentState(
      StateKeyEnum.PREFERRED_MARKET_FILTER,
      preferredMarketFilter
    );
  }, [preferredMarketFilter]);

  useEffect(() => {
    setPersistentState(StateKeyEnum.SHOW_BOTTOM_TRAY, showBottomTray ? 1 : 0);
  }, [showBottomTray]);

  useEffect(() => {
    setPersistentState(
      StateKeyEnum.TRADE_USE_USDC_TERMS,
      tradeUseUSDCTerms ? 1 : 0
    );
  }, [tradeUseUSDCTerms]);

  useEffect(() => {
    setPersistentState(StateKeyEnum.ORDERBOOK_LAYOUT, orderbookLayout);
  }, [orderbookLayout]);

  return {
    showOnlyCurrentMarket,
    setShowOnlyCurrentMarket,
    orderbookViewInUSD,
    setOrderbookViewInUSD,
    preferredMarketFilter,
    setPreferredMarketFilter,
    showBottomTray,
    setShowBottomTray,
    tradeUseUSDCTerms,
    setTradeUseUSDCTerms,
    orderbookLayout,
    setOrderbookLayout,
  };
};

export default usePersistentState;
